import { ReactElement, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Skeleton from '../../components/Skeleton';
import SectionHeader from '../../components/SectionHeader';
import { useSessions } from '../../hooks/useSessions';
import { useRidersById } from '../../hooks/useRiders';
import { HiOutlinePlusCircle } from 'react-icons/hi2';
import { SessionAnalysisTypeUtils } from '../../types/SessionAnalysisType';
import { SessionIcon } from '../../utils/sessionIconUtils';
import SessionColorIndicator from '../../components/SessionColorIndicator';
import { compareSessionsDesc } from '../../utils/sessionSort';

export default function SessionList(): ReactElement {
  const { isPending: isSessionsPending, isFetching: isSessionsFetching, data: sessions } = useSessions();
  const { data: ridersById } = useRidersById();
  const [showHidden, setShowHidden] = useState(false);

  // Set page background color
  useEffect(() => {
    document.documentElement.style.setProperty('--page-bg-color', '#f3f4f6'); // gray-100
    return () => {
      document.documentElement.style.setProperty('--page-bg-color', 'transparent');
    };
  }, []);

  const entry = (label: string, value?: string) => {
    return (
      <div className="cr-text-base">
        <div className="cr-inline cr-break-after-auto cr-font-bold cr-uppercase">{label}:&nbsp; </div>
        <div className="cr-inline cr-font-normal cr-font-main">
          {value}
        </div>
      </div>
    );
  };

  const sessionsArray = Array.isArray(sessions) ? sessions : [];
  const grid = sessionsArray
    .filter((session: any) => !SessionAnalysisTypeUtils.isSingletonLevel(session.analysis_type))
    .filter((session: any) => showHidden || !session.hidden)
    ?.sort((a: any, b: any) => compareSessionsDesc(a, b, ridersById))
    .map((session: any) => (
      <Link
        to={`/sessions/${session.id}`}
        key={session.id}
        className={
          'cr-relative cr-flex cr-flex-col cr-gap-4 cr-rounded-2xl cr-bg-white cr-p-4 cr-text-cirrusBlue-400 ' +
          `cr-no-underline cr-shadow-md md:cr-pb-5 lg:cr-pb-20${session.hidden ? ' cr-opacity-60' : ''}`
        }
      >
        <div className="cr-flex cr-items-center cr-gap-3">
          <SessionIcon analysisType={session.analysis_type} size={32} className="cr-text-cirrusBlue-400" />
          <div className="font-montserrat cr-text-2xl cr-font-semibold">{session.date}</div>
        </div>
        {entry('Rider', ridersById?.get(session.rider_id)?.nickname)}
        {entry('Description', session.description)}
        <SessionColorIndicator sessionId={session.id} />
      </Link>
    ));
  return (
    <>
      <SectionHeader title="Sessions" isLoading={isSessionsFetching} />
      <div className="cr-mb-4">
        <label className="cr-flex cr-w-fit cr-cursor-pointer cr-items-center cr-gap-2 cr-font-main cr-text-base">
          <input
            type="checkbox"
            checked={showHidden}
            onChange={(e) => setShowHidden(e.target.checked)}
            className="cr-cursor-pointer"
            style={{ accentColor: '#03365D', width: '18px', height: '18px' }}
          />
          Show hidden
        </label>
      </div>
      <Skeleton isPending={isSessionsPending}>
        <div className="cr-flex cr-flex-col cr-gap-6 md:cr-grid md:cr-grid-cols-4">
          <Link
            to={'/sessions/new'}
            key={'new'}
            className="font-montserrat cr-flex cr-flex-col cr-items-center cr-gap-4 cr-rounded-2xl cr-bg-white
                       cr-p-4 cr-text-center cr-text-xl cr-font-semibold cr-text-cirrusBlue-400 cr-no-underline cr-shadow-md md:cr-pb-5 lg:cr-p-5"
          >
            New Session
            <HiOutlinePlusCircle size={80} />
          </Link>
          {grid}
        </div>
      </Skeleton>
    </>
  );
}
